'use client';

import { useEffect, useRef, useState } from 'react';
/**
 * Takes section ids and tracks which section is nearest
 * the top of the viewport (below the sticky header + ToC) using an
 * `IntersectionObserver`, so the ToC can highlight it as the user scrolls.
 *
 * An IntersectionObserver callback only reports entries whose intersection status just
 * changed, not the full current state of everything being observed - so reacting only to
 * the entries in one batch misses two things: a batch containing only an exit (nothing
 * entering alongside it) has no candidate to fall back to, and a batch where two adjacent
 * sections are simultaneously intersecting (they're tall relative to the viewport, so this
 * happens right at the boundary between them) has no way to prefer one over the other.
 *
 * This tracks each section's last-known intersecting state in a ref, updated incrementally
 * from every entry, and recomputes the active section from that full current state on every
 * callback: the last id (in document order) still intersecting, since sectionIds is already
 * top-to-bottom, so that's the one the user has scrolled furthest into.
 */
export const useActiveSection = ({ sectionIds, rootMargin }: { sectionIds: string[]; rootMargin: string }) => {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const intersectingIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    intersectingIds.current = new Set();

    const sectionElements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (!sectionElements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            intersectingIds.current.add(entry.target.id);
          } else {
            intersectingIds.current.delete(entry.target.id);
          }
        });

        // Tie breaker logic if multiple sections are intersecting at the same time
        const stillIntersecting = sectionIds.filter((id) => intersectingIds.current.has(id));

        if (stillIntersecting.length) {
          setActiveSectionId(stillIntersecting[stillIntersecting.length - 1]);
        }
      },
      { rootMargin, threshold: 0 },
    );

    sectionElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [sectionIds, rootMargin]);

  return activeSectionId;
};

/** Scrolls a section into view, respecting its `scroll-margin-top` for the sticky header + ToC. */
export const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
